import * as THREE from "three";
import { OBJLoader, STLLoader } from "three-stdlib";
import type { ModelFormat } from "../types";
import { base64ToArrayBuffer } from "./binary";

const stlLoader = new STLLoader();
const objLoader = new OBJLoader();

const geometryCache = new Map<string, THREE.BufferGeometry>();

/** Parses the stored base64 file payload into a BufferGeometry, cached per object id. */
export function getGeometry(id: string, fileBase64: string, format: ModelFormat): THREE.BufferGeometry {
  const cached = geometryCache.get(id);
  if (cached) return cached;

  const buffer = base64ToArrayBuffer(fileBase64);
  let geometry: THREE.BufferGeometry;

  if (format === "stl") {
    geometry = stlLoader.parse(buffer);
    // STL files are conventionally authored Z-up (CAD/printing plate); the scene is Y-up.
    geometry.rotateX(-Math.PI / 2);
  } else {
    const text = new TextDecoder().decode(buffer);
    const group = objLoader.parse(text);
    const geometries: THREE.BufferGeometry[] = [];
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        geometries.push(child.geometry);
      }
    });
    geometry =
      geometries.length === 1
        ? geometries[0]
        : mergeGeometries(geometries);
  }

  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  const box = geometry.boundingBox!;
  // Center horizontally (X/Z), but rest the object's bottom exactly on the floor (Y=0).
  geometry.translate(-(box.min.x + box.max.x) / 2, -box.min.y, -(box.min.z + box.max.z) / 2);
  geometry.computeBoundingBox();
  geometryCache.set(id, geometry);
  return geometry;
}

function mergeGeometries(geometries: THREE.BufferGeometry[]): THREE.BufferGeometry {
  if (geometries.length === 0) return new THREE.BufferGeometry();
  const merged = new THREE.BufferGeometry();
  const positions: number[] = [];
  for (const geo of geometries) {
    const pos = geo.getAttribute("position");
    for (let i = 0; i < pos.count; i++) {
      positions.push(pos.getX(i), pos.getY(i), pos.getZ(i));
    }
  }
  merged.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  return merged;
}

export function invalidateGeometryCache(id: string) {
  geometryCache.delete(id);
}

export function detectFormat(filename: string): ModelFormat | null {
  const ext = filename.split(".").pop()?.toLowerCase();
  if (ext === "stl") return "stl";
  if (ext === "obj") return "obj";
  return null;
}
